import { nanoid } from "nanoid";
import { createSelector } from "reselect";

import { normalizeCIF } from "@app/components/contractors/helpers";
import { EMenuCmdTab } from "@app/state/constants";
import { TContractor, TDBContractor } from "@app/types/insider";

import { schema } from "../schema";

import type { AppState } from "../xstore";
import type { TContractorFormSource } from "@app/types/insider";
import type { TISOCountry } from "@app/types/insider";
import type { IListofContacts } from "@app/types/insider";
function createParameterSelector(selector: any) {
  return (_: AppState, params: any) => selector(params);
}
const getFilesByArchivesFileHeadId = createParameterSelector(
  (params: { FSHID: number }) => params?.FSHID || null
);

const getFXMID = createParameterSelector(
  (params: { FXMID: number }) => params?.FXMID || null
);
const getBySourceName = createParameterSelector(
  (params: { source: TContractorFormSource }) =>
    params?.source as TContractorFormSource
);

const getCountryCode = createParameterSelector(
  (params: { CountryCode: string }) => params?.CountryCode || null
);
const getCountrySubdivision = createParameterSelector(
  (params: { CountyCode: string }) => params?.CountyCode || null
);

export const defaultEmptyContractor: TDBContractor = {
  CFF_ID: -1,
  C_ID: 0,
  DENUMIRE_F: "",
  ADRS_F: "",
  JUDETUL: "",
  NR_INREG: "",
  CIF: "",
  IBAN: "",
  BANCA: "",
  INATENTIA: "",
  FAX: "",
  AMA_ID_INS: 0,
  DATA_INS: "",
  CFF_EMAIL: "",
  CFF_COD_POSTAL: "",
  CIF_NORO: "",
  ORAS: "",
  COD_POSTAL: "",
  COD_JUDET: "",
  ADRESA_SUPLIMENTARA: "",
  COD_TARA: "",
  TELEFON: "",
  BUYER_REFERENCE: "",
  USING_RO_EFACTURA: 1,
};

export const tDbContractorToContractor = (
  dbContractor: TDBContractor
): TContractor => {
  const {
    CFF_ID,
    C_ID,
    DENUMIRE_F,
    ADRS_F,
    JUDETUL,
    NR_INREG,
    CIF,
    IBAN,
    BANCA,
    INATENTIA,
    FAX,
    AMA_ID_INS,
    CFF_EMAIL,
    CFF_COD_POSTAL,
    CIF_NORO,
    ORAS,
    COD_POSTAL,
    COD_JUDET,
    ADRESA_SUPLIMENTARA,
    COD_TARA,
    TELEFON,
    BUYER_REFERENCE,
    USING_RO_EFACTURA,
    __alertaNeplatitorTVA,
  } = dbContractor;
  return {
    __type: "CONTRACTOR",
    __messageID: CFF_ID,
    CffId: CFF_ID,
    ClientName: DENUMIRE_F,
    Address: ADRS_F,
    County: JUDETUL,
    RegistrationNumber: NR_INREG,
    TaxCode: CIF,
    Iban: IBAN,
    BankName: BANCA,
    Attention: INATENTIA,
    Phone: TELEFON,
    Fax: FAX,
    ClientId: C_ID,
    AmaIdIns: AMA_ID_INS,
    City: ORAS,
    CountryCode: COD_TARA,
    CountyCode: COD_JUDET,
    Email: CFF_EMAIL,
    PostalCode: CFF_COD_POSTAL,
    AdditionalAddress: ADRESA_SUPLIMENTARA,
    BuyerReference: BUYER_REFERENCE,
    UsingRoEfactura: USING_RO_EFACTURA,
    __alertaNeplatitorTVA: __alertaNeplatitorTVA,
  };
};
export const tContractorFromDbContractorAsSupplier = (
  dbContractor: TDBContractor
): TContractor => {
  const result = tDbContractorToContractor(dbContractor);
  result.__type = "SUPPLIER";
  return result;
};
export const tContractorFromDbContractorAsCustomer = (
  dbContractor: TDBContractor
): TContractor => {
  const result = tDbContractorToContractor(dbContractor);
  result.__type = "CUSTOMER";
  return result;
};
export const tContractorFormData = (
  contractor: TContractor,
  messageID: number,
  num: number
): TContractor => {
  const [addressLine1, addressLine2] = (contractor?.Address || "").split("||");
  const newContractor = {
    ...contractor,
    Address: addressLine1,
    __messageID: messageID,
    __address__continuation: addressLine2,
    __num: num,
  };
  return newContractor;
};

const idxContractor = createSelector(
  schema.contractors_cff.selectTableAsList,
  (ctors) => {
    return ctors.reduce((acc, item) => {
      if (!item.CIF) return acc;
      acc[normalizeCIF(item.CIF)] = item.CFF_ID;
      return acc;
    }, {});
  }
);
export const selectCurrentClientCFF = createSelector(
  schema.uiSpvMain.select,
  schema.SPVMessageKeys.selectTable,
  schema.contractors_cff.selectTable,
  schema.contractors_cff_num.select,
  idxContractor,
  (currentMessage, msgKeys, contractorsCFF, num, idx): TContractor => {
    const { currentRecord } = currentMessage;
    const fmxid = currentRecord.FXM_ID;

    const ctrctorCIF =
      currentRecord.QRESULT_TYPE === "FACTURA PRIMITA"
        ? currentRecord.MATCHED_EMITENT
        : currentRecord.MATCHED_BENEFICIAR;

    const ctrctorCIFNORO = (ctrctorCIF || "").replace("RO", "").trim();

    //earlyest
    const clearMatch =
      ctrctorCIFNORO && contractorsCFF?.[idx?.[ctrctorCIFNORO]];
    if (clearMatch) {
      return currentRecord.QRESULT_TYPE === "FACTURA PRIMITA"
        ? tContractorFormData(
            tContractorFromDbContractorAsSupplier(clearMatch),
            fmxid,
            num
          )
        : tContractorFormData(
            tContractorFromDbContractorAsCustomer(clearMatch),
            fmxid,
            num
          );
    }

    const bindings = msgKeys[fmxid];

    if (
      !bindings ||
      (bindings?.CFF_ID || -1) < 0 ||
      !contractorsCFF?.[bindings.CFF_ID]
    ) {
      const early = {
        ...defaultEmptyContractor,
        __type:
          currentRecord.QRESULT_TYPE === "FACTURA PRIMITA"
            ? "SUPPLIER"
            : "CUSTOMER",
        CIF: ctrctorCIF,
        CIF_NORO: ctrctorCIFNORO,
      };
      return early.__type === "SUPPLIER"
        ? tContractorFormData(
            tContractorFromDbContractorAsSupplier(early),
            fmxid,
            num
          )
        : tContractorFormData(
            tContractorFromDbContractorAsCustomer(early),
            fmxid,
            num
          );
    }
    const contractor = contractorsCFF[bindings.CFF_ID] as TDBContractor;

    return currentRecord.QRESULT_TYPE === "FACTURA PRIMITA"
      ? tContractorFormData(
          tContractorFromDbContractorAsSupplier(contractor),
          fmxid,
          num
        )
      : tContractorFormData(
          tContractorFromDbContractorAsCustomer(contractor),
          fmxid,
          num
        );
  }
);

export const selectCurrentClientTMP = createSelector(
  schema.uiSpvMain.select,
  schema.SPVMessageKeys.selectTable,
  schema.contractors_temp.selectTable,
  schema.contractors_temp_num.select,
  (currentMessage, msgKeys, contractorsTMP, num): TContractor => {
    const { otherCurrentRecord: currentRecord } = currentMessage;
    const fmxid = currentRecord.FXM_ID;
    const ctrctorCIF =
      currentRecord.QRESULT_TYPE === "FACTURA PRIMITA"
        ? currentRecord.MATCHED_EMITENT
        : currentRecord.MATCHED_BENEFICIAR;
    const ctrctorCIFNORO = (ctrctorCIF || "").replace("RO", "").trim();
    const bindings = msgKeys[fmxid];
    if (
      // !bindings ||
      // (bindings?.CFFTMP_ID || -1) < 0 ||
      // !contractorsTMP?.[bindings.CFFTMP_ID]
      !contractorsTMP?.[ctrctorCIFNORO]
    ) {
      const early = {
        ...defaultEmptyContractor,
        __type:
          currentRecord.QRESULT_TYPE === "FACTURA PRIMITA"
            ? "SUPPLIER"
            : "CUSTOMER",
        CIF: ctrctorCIF,
        CIF_NORO: ctrctorCIFNORO,
      };
      return early.__type === "SUPPLIER"
        ? tContractorFormData(
            tContractorFromDbContractorAsSupplier(early),
            fmxid,
            num
          )
        : tContractorFormData(
            tContractorFromDbContractorAsCustomer(early),
            fmxid,
            num
          );
    }
    // const contractor = contractorsTMP[bindings.CFFTMP_ID] as TDBContractor;
    const contractor = contractorsTMP[ctrctorCIFNORO] as TDBContractor;
    return currentRecord.QRESULT_TYPE === "FACTURA PRIMITA"
      ? tContractorFormData(
          tContractorFromDbContractorAsSupplier(contractor),
          fmxid,
          num
        )
      : tContractorFormData(
          tContractorFromDbContractorAsCustomer(contractor),
          fmxid,
          num
        );
  }
);

export const selectCurentClientAnaf = createSelector(
  schema.uiSpvMain.select,
  schema.contractors_anaf.selectTable,
  schema.contractors_anaf_num.select,
  (currentMessage, contractorsANAF, num): TContractor => {
    const { currentRecord } = currentMessage;
    const fmxid = currentRecord.FXM_ID;
    const ctrctorCIF =
      currentRecord.QRESULT_TYPE === "FACTURA PRIMITA"
        ? currentRecord.MATCHED_EMITENT
        : currentRecord.MATCHED_BENEFICIAR;
    const ctrctorCIFNORO = (ctrctorCIF || "").replace("RO", "").trim();
    if (
      !contractorsANAF ||
      !Object.values(contractorsANAF).length ||
      !contractorsANAF[ctrctorCIFNORO]
    ) {
      const early = Object.assign({}, defaultEmptyContractor, {
        __type:
          currentRecord.QRESULT_TYPE === "FACTURA PRIMITA"
            ? "SUPPLIER"
            : "CUSTOMER",
        CIF: ctrctorCIF,
        CIF_NORO: ctrctorCIFNORO,
        __alertaNeplatitorTVA: undefined,
      });
      // const early = {
      //   ...defaultEmptyContractor,
      //   __type:
      //     currentRecord.QRESULT_TYPE === 'FACTURA PRIMITA'
      //       ? 'SUPPLIER'
      //       : 'CUSTOMER',
      //   CIF: ctrctorCIF,
      //   CIF_NORO: ctrctorCIFNORO,
      //   __alertaNeplatitorTVA: undefined,
      // };
      return early.__type === "SUPPLIER"
        ? tContractorFormData(
            tContractorFromDbContractorAsSupplier(early),
            fmxid,
            num
          )
        : tContractorFormData(
            tContractorFromDbContractorAsCustomer(early),
            fmxid,
            num
          );
    }
    const contractor = contractorsANAF[ctrctorCIFNORO] as TDBContractor;
    return currentRecord.QRESULT_TYPE === "FACTURA PRIMITA"
      ? tContractorFormData(
          tContractorFromDbContractorAsSupplier(contractor),
          fmxid,
          num
        )
      : tContractorFormData(
          tContractorFromDbContractorAsCustomer(contractor),
          fmxid,
          num
        );
  }
);

const parseIntSafe = (val: string) => {
  //if is not a number return -1
  try {
    return parseInt(val);
  } catch (e) {
    return -1;
  }
};
export const mapContactListToContractor = (
  contact: IListofContacts
): TContractor => {
  if (!contact) {
    return tDbContractorToContractor(defaultEmptyContractor);
  }
  return {
    __type: "CONTRACTOR",
    __messageID: -1,
    CffId: undefined,
    ClientName: contact.DENUMIRE,
    Address: contact.ADRESE,
    County: "",
    RegistrationNumber: contact.NR_INREG,
    TaxCode: contact.CIF,
    Iban: contact.IBAN,
    BankName: contact.BANCA,
    Attention: "", // <<< further map //
    Phone: contact.TELEFON,
    Fax: contact.FAX,
    ClientId: contact.C_ID,
    AmaIdIns: parseIntSafe(contact.AVO_INSERTED),
    City: "",
    CountryCode: "",
    CountyCode: "",
    Email: contact.EMAIL,
    PostalCode: "",
    AdditionalAddress: "",
    BuyerReference: "",
    UsingRoEfactura: 1, // 0 or 1 (-1 neutral on update)
    __num: 0,
  };
};

const selectCurrentDBConactListItem = createSelector(
  schema.contactlist.selectTable,
  schema.uiSpvGrid.select,
  schema.contcatlist_num.select,
  schema.uiSpvMain.select,
  (contact, selection, num, mainState): TContractor => {
    const { selectedContactID } = selection;
    const fmxid = mainState.currentRecord?.FXM_ID;
    return contact?.[selectedContactID]
      ? tContractorFormData(
          mapContactListToContractor(contact[selectedContactID]),
          fmxid,
          num
        )
      : tContractorFormData(
          tDbContractorToContractor(defaultEmptyContractor),
          fmxid,
          num
        );
  }
);

const selectCurrentAnafContractrByCIFSelected = createSelector(
  schema.uiContractorsEditing.select,
  schema.contractors_anaf.selectTable,
  (editingState, dataset) => {
    const cif = editingState.searchCIF;
    const normCif = normalizeCIF(cif);
    if (!normCif || !dataset[normCif]) {
      return tDbContractorToContractor(defaultEmptyContractor);
    }
    return tContractorFormData(
      tDbContractorToContractor(dataset[normCif]),
      -1,
      -1
    );
  }
);

const selectCurrentContractorTemp = createSelector(
  schema.uiContractorsEditing.select,
  schema.contractors_temp.selectTableAsList,
  (editingState, contractors) => {
    const cif = editingState.searchCIF;
    const normCif = normalizeCIF(cif);
    const ctctor = contractors.find((item) => {
      return item.CIF_NORO === normCif;
    });

    if (!ctctor) {
      return tContractorFormData(
        tDbContractorToContractor(defaultEmptyContractor),
        -1,
        -1
      );
    }
    return tContractorFormData(tDbContractorToContractor(ctctor), -1, -1);
  }
);

// export const selectCurrentContractor = createSelector(
// ......
// );
// ((_, p) => {
//   const ts = _.uiJGBrowserHeader.tabSelected;
//   console.log('ts', ts)
//   return `${p.source}::${ts}` || "empty";
// }); // slab

export const selectCurrentContractor = createSelector(
  selectCurrentClientTMP,
  selectCurentClientAnaf,
  selectCurrentDBConactListItem,
  schema.uiJGBrowserHeader.select,
  selectCurrentAnafContractrByCIFSelected,
  selectCurrentContractorTemp,
  getBySourceName,
  (clientTMP, clientANAF, contactItem, jgHeader, anafIe, tmpIe, source) => {
    let tabSelected;

    if (process.env.REACT_APP_MODE === "DELPHI") {
      // Extract from window.location.hash since the keyword parameter is there
      const hashParams = new URLSearchParams(
        window.location.hash.split("?")[1]
      ); // Split by '#' and take the second part to get query string
      tabSelected = hashParams.get("keyword"); // Use the 'keyword' parameter from the hash
    } else {
      tabSelected = jgHeader?.tabSelected; // Use Redux state in React mode
    }
    const isIe = tabSelected === EMenuCmdTab.CMD_BGIM_CLIENTS;
    switch (source) {
      case "CFF_TMP":
        return isIe ? tmpIe : clientTMP; // Use isIe to decide between insert client mode and SPV message mode
      case "ANAF":
        return [
          EMenuCmdTab.CMD_BGIM_CLIENTS,
          EMenuCmdTab.CMD_BGIM_EMITENT,
        ].includes(tabSelected)
          ? anafIe // In insert client mode
          : clientANAF; // In SPV message mode
      case "CONTACTE":
        return contactItem;
      default:
        return defaultEmptyContractor; // Return a default if none of the cases match
    }
  }
);

export const selectCountries = createSelector(
  schema.iso3166_2.selectTableAsList,
  (iso3166_2) => {
    const iso = iso3166_2.reduce((acc, item) => {
      acc[item.COD_NATIONAL] = {
        COD_NATIONAL: item.COD_NATIONAL,
        DEN_NATIONAL: item.DEN_NATIONAL,
      };
      return acc;
    }, {});
    return Object.values(iso);
  }
);

/* 
  COD_SUBDIV: string;
  DEN_SUBDIV: string; 
*/

export const selectCountrySubdivisions = createSelector(
  schema.iso3166_2.selectTableAsList,
  getCountryCode,
  (iso3166_2, countryCode) => {
    if (!countryCode) {
      return [];
    }
    const data = iso3166_2
      .filter((item) =>
        item.COD_NATIONAL === "RO"
          ? // filter out SECTOR1 .. 5
            item.COD_NATIONAL === countryCode &&
            !["RO-B", "", null].includes(item.COD_SUBDIV)
          : item.COD_NATIONAL === countryCode
      )
      .map((item) => ({
        COD_SUBDIV: item.COD_SUBDIV,
        DEN_SUBDIV: item.DEN_SUBDIV,
      }));
    // add generic Bucuresti as a subdivision
    return (
      countryCode === "RO"
        ? [{ COD_SUBDIV: "RO-B", DEN_SUBDIV: "Bucuresti" }]
        : []
    ).concat(data);
  }
);
//((_, countryCode) => countryCode||'empty');
/* 

  ISOID: number;
  COD_NATIONAL: string;
  DEN_NATIONAL: string;
  COD_SUBDIV: string;
  DEN_SUBDIV: string;
*/
export const selectCity = createSelector(
  schema.iso3166_2.selectTableAsList,
  schema.isoNewCities.selectTableAsList,
  selectCurrentClientCFF,
  getCountrySubdivision,
  (iso3166_2, newCities, currentClientCff, countyCode) => {
    const currentCountryCode = currentClientCff.CountryCode;
    const currentCountryName = iso3166_2.find(
      (item) => item.COD_NATIONAL === currentCountryCode
    )?.DEN_NATIONAL;
    const currentSubdivision = currentClientCff.CountyCode;
    const currentCity = currentClientCff.City;
    const aKey = nanoid(7);

    const currentEntry: TISOCountry = {
      ISOID: aKey,
      COD_NATIONAL: currentCountryCode,
      DEN_NATIONAL: currentCountryName,
      COD_SUBDIV: currentSubdivision,
      DEN_SUBDIV: currentCity,
    };
    // console.log('countyCode in selector', countyCode)
    return countyCode === "RO-B"
      ? iso3166_2
          .filter((item) => item.COD_SUBDIV === countyCode)
          .map((item) => ({
            COD_SUBDIV: item.COD_SUBDIV,
            DEN_SUBDIV: item.DEN_SUBDIV,
          }))
      : Object.values(
          newCities.concat(currentEntry).reduce((acc, item) => {
            //filter out the subdivisions that do not match the current country code
            if (item.COD_SUBDIV !== countyCode) {
              return acc;
            }
            //create a selection by unique city names
            acc[item.DEN_SUBDIV] = item;
            return acc;
          }, {}) as Record<string, TISOCountry>
        ).map((item) => ({
          COD_SUBDIV: item.COD_SUBDIV,
          DEN_SUBDIV: item.DEN_SUBDIV,
        }));
  }
);

export const selectCurrentMirroredTab = createSelector(
  schema.uiSpvGrid.select,
  (s) => {
    return s.selectedCtorMirrorTab;
  }
);

export const selectSelectedContactID = createSelector(
  schema.uiSpvGrid.select,
  (s) => {
    return s.selectedContactID;
  }
);

export const contactSBoxDataset = createSelector(
  schema.contactlist.selectTableAsList,
  (contactList) => {
    return contactList.map((item) => ({
      value: item.C_ID,
      text: item.DENUMIRE,
    }));
  }
);

//todo: read the invoice raw data from the database as show a short summary of the invoice

/* 

C_ID(pin):106
DENUMIRE(pin):"Anghel & Stabb - S.C. ARDWERK SERVICES SRL"
LEGAL_FORM(pin):""
ADRESE(pin):"Sediu: Str. Dr. Herescu 15, et. 1si 2, sector 5 Bucuresti, Cotroceni; "
TELEFON(pin):"0 21 410 48 85"
FAX(pin):"0 21 410 23 71"
EMAIL(pin):""
WEB_SITE(pin):""
OBSERVATII(pin):""
CERINTE_SPECIALE(pin):""
DT_INSERTED(pin):null
AVO_INSERTED(pin):""
DT_UPDATED(pin):null
AVO_UPDATED(pin):""

*/
